<script>
import { Bar } from "vue-chartjs";
import Chart from "chart.js";
import ChartAnnotationsPlugin from "chartjs-plugin-annotation";
Chart.plugins.register(ChartAnnotationsPlugin);

const ITEM_KEYS = ["VWC", "PF", "Temperature"];

export default {
  extends: Bar,
  name: "SoilChart",
  props: {
    timeSeries: {
      type: Array
    },
    displayItem: String,
    displayInterval: Number
  },
  data() {
    return {
      dataCollection: {
        labels: [],
        datasets: [
          {
            type: "line",
            label: "体積含水率",
            unit: "m³/m³",
            pointBackgroundColor: "white",
            fill: false,
            yAxisID: "vwc",
            borderWidth: 2,
            borderColor: "#3D8AFF",
            backgroundColor: "#3D8AFF",
            pointBorderColor: "#3D8AFF",
            pointRadius: 2,
            pointHitRadius: 0,
            lineTension: 0,
            data: [],
            spanGaps: true
          },
          {
            type: "line",
            label: "PF値",
            unit: "",
            pointBackgroundColor: "white",
            fill: false,
            yAxisID: "pf",
            borderWidth: 2,
            borderColor: "#91C46C",
            backgroundColor: "#91C46C",
            pointBorderColor: "#91C46C",
            pointRadius: 2,
            pointHitRadius: 0,
            lineTension: 0,
            data: [],
            spanGaps: true
          },
          {
            type: "line",
            label: "温度",
            unit: "℃",
            pointBackgroundColor: "white",
            fill: false,
            yAxisID: "temperature",
            borderWidth: 2,
            borderColor: "#01163F",
            backgroundColor: "#01163F",
            pointBorderColor: "#01163F",
            pointRadius: 2,
            pointHitRadius: 0,
            lineTension: 0,
            data: [],
            spanGaps: true
          }
        ]
      },
      options: {
        layout: {
          padding: {
            right: 0,
            left: 0
          }
        },
        tooltips: {
          enabled: true,
          mode: "index",
          intersect: false,
          callbacks: {
            label: function(tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label;
              const unit = data.datasets[tooltipItem.datasetIndex].unit;
              return (
                label +
                " : " +
                data.datasets[tooltipItem.datasetIndex].data[
                  tooltipItem.index
                ] +
                " " +
                unit
              );
            }
          }
        },
        scales: {
          yAxes: [
            {
              id: "vwc",
              position: "left",
              gridLines: {
                display: false
              },
              ticks: {
                beginAtZero: true
              }
            },
            {
              id: "pf",
              position: "left",
              gridLines: {
                display: true
              },
              ticks: {
                min: 0,
                max: 7,
                stepSize: 1
              }
            },
            {
              id: "temperature",
              position: "right",
              gridLines: {
                display: false
              }
            }
          ],
          xAxes: [
            {
              id: "data",
              type: "time",
              gridLines: {
                display: false
              },
              ticks: {
                display: true,
                autoSkip: false
              },
              time: {
                unit: "minute",
                stepSize: 10,
                tooltipFormat: "MM/DD HH:mm",
                displayFormats: {
                  minute: "HH:mm",
                  hour: "HH:mm",
                  day: "MM/DD"
                }
              },
              categoryPercentage: 1.0,
              barPercentage: 1.0
            }
          ]
        },
        legend: {
          display: false
        },
        responsive: true,
        maintainAspectRatio: false,
        hover: {
          mode: "index",
          intersect: false
        },
        groundDataset: [],
        annotation: {
          events: ["click", "dblclick", "mouseover", "mouseout"],
          annotations: [
            {
              type: "line",
              mode: "vertical",
              scaleID: "data",
              value: "02:00",
              borderColor: "#FF0000",
              borderWidth: 1.5,
              label: {
                content: "現在",
                position: "top",
                enabled: true
              }
            }
          ]
        }
      }
    };
  },
  computed: {
    baseDate() {
      return this.$store.getters.baseDate.clone();
    },
    minDate() {
      const d = this.baseDate.clone();
      if (this.displayInterval === 10) {
        d.subtract(3, "hours");
      } else if (this.displayInterval === 60) {
        d.subtract(2, "days");
      } else {
        d.subtract(14, "days");
      }
      return d.subtract(d.minute() % 10, "minutes");
    },
    maxDate() {
      if (this.displayInterval === 10) {
        return this.baseDate.clone().add(10, "minutes");
      } else if (this.displayInterval === 60) {
        return this.baseDate.clone().add(3, "hours");
      } else {
        return this.baseDate.clone().add(1, "days");
      }
    }
  },
  watch: {
    timeSeries() {
      this.reRender();
    },
    displayItem() {
      this.reRender();
    }
  },
  mounted() {
    this.createChart();
    setTimeout(() => {
      this.renderChart(this.dataCollection, this.options);
    }, 500);
  },
  methods: {
    reRender() {
      this.createChart();
      this.renderChart(this.dataCollection, this.options);
    },
    createChart() {
      // labels
      this.dataCollection.labels = this.timeSeries.map(ts => ts.chartDate);
      this.options.scales.xAxes[0].ticks.min = this.minDate.toDate();
      this.options.scales.xAxes[0].ticks.max = this.maxDate.toDate();
      this.options.scales.xAxes[0].time.unit =
        this.displayInterval === 10
          ? "minute"
          : this.displayInterval === 60
          ? "hour"
          : "day";
      this.options.scales.xAxes[0].time.stepSize =
        this.displayInterval === 10 ? 10 : 1;

      // data
      this.dataCollection.datasets[0].data = this.timeSeries.map(ts => ts.VWC);
      this.dataCollection.datasets[1].data = this.timeSeries.map(ts => ts.PF);
      this.dataCollection.datasets[2].data = this.timeSeries.map(
        ts => ts.Temperature
      );
      this.options.groundDataset = this.landform;

      // displayItem: 空なら全項目、指定時は該当項目のみ表示
      ITEM_KEYS.forEach((key, i) => {
        const visible = this.displayItem === "All" || this.displayItem === key;
        this.dataCollection.datasets[i].hidden = !visible;
        this.options.scales.yAxes[i].display = visible;
        this.options.scales.yAxes[i].gridLines.display =
          this.displayItem === key || key === "PF";
      });
    }
  }
};
</script>

<style scoped></style>
